import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import React, { Suspense, useLayoutEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Box, Container, Heading, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Home from "./pages/Home";
import Contact from "./pages/Contact";
import Articles from "./pages/Articles";
import LogicielATS from "./pages/resource/LogicielATS";
import SuccessPage from "./pages/Evaluate/SuccessPage";
import WelcomePage from "./pages/Evaluate/WelcomePage";
import StartEvaluation from "./pages/Evaluate/StartEvaluation";
import QuestionPage from "./pages/Evaluate/QuestionPage";
import MicCheckPage from "./pages/Evaluate/MicCheckPage";
import SampleQuestionPage from "./pages/Evaluate/SampleQuestionPage";
import SubmitResults from "./pages/Evaluate/SubmitResults";
import MentionsLegales from "./pages/resource/MentionsLegales"; 
import Politique from "./pages/resource/Politique";
import GroupSummary from "./pages/Evaluate/GroupSummary";
import SignUp from "./pages/Auth/SignUp";
import Login from "./pages/Auth/Login";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import PortalLayout from "./components/Portal/PortalLayout";
import Dashboard from "./pages/Portal/Dashboard";
import Settings from "./pages/Portal/Settings";
import ProfileSettings from "./pages/Portal/ProfileSettings";
import AsapCV from "./pages/Portal/AsapCV";
import AsapSkills from "./pages/Portal/AsapSkills";
import AsapProfile from "./pages/Portal/AsapProfile";
import ComingSoon from "./pages/Portal/ComingSoon";
import PrivateRoute from "./components/Routes/PrivateRoute";
import AdminRoute from "./components/Routes/AdminRoute";
import FeatureRoute from "./components/Routes/FeatureRoute";
import SuperAdminRoute from "./components/Routes/SuperAdminRoute";
import InternalRoute from "./components/Routes/InternalRoute";
import ConsultantRoute from "./components/Routes/ConsultantRoute";
import PortalIndexRedirect from "./components/Routes/PortalIndexRedirect";
import SuperAdminLayout from "./components/SuperAdmin/SuperAdminLayout";
import AdminDashboard from "./pages/SuperAdmin/Statistics";
import Configuration from "./pages/SuperAdmin/Configuration";
import CompanyDetail from "./pages/SuperAdmin/CompanyDetail";
import Templates from "./pages/SuperAdmin/Templates";
import Users from "./pages/SuperAdmin/Users";
import SessionExpiredModal from "./components/Portal/SessionExpiredModal";

// Module pages
import AsapCVModule from "./pages/modules/AsapCV";
import AsapSkillsModule from "./pages/modules/AsapSkills";
import AsapPilot from "./pages/modules/AsapPilot";
import AsapPartners from "./pages/modules/AsapPartners";
import AsapProfil from "./pages/modules/AsapProfil";
import PartnersMissions from "./pages/Portal/Partners/PartnersMissions";
import PartnersIncoming from "./pages/Portal/Partners/PartnersIncoming";
import PartnersPoolsAndCompanies from "./pages/Portal/Partners/PartnersPoolsAndCompanies";
import PartnersLandingWelcome from "./pages/Partners/LandingWelcome";
import PartnersLandingForm from "./pages/Partners/LandingForm";
import PartnersLandingSuccess from "./pages/Partners/LandingSuccess";
import PartnersUnsubscribe from "./pages/Partners/Unsubscribe";
import PilotClients from "./pages/Portal/Pilot/Clients";
import PilotNeeds from "./pages/Portal/Pilot/Needs";
import NeedDetail from "./pages/Portal/Pilot/NeedDetail";
import PilotConsultants from "./pages/Portal/Pilot/Consultants";
import ClientDetail from "./pages/Portal/Pilot/ClientDetail";
import ConsultantDetail from "./pages/Portal/Pilot/ConsultantDetail";
import PilotMatches from "./pages/Portal/Pilot/Matches";
import MatchDetail from "./pages/Portal/Pilot/MatchDetail";
import ConsultantTracking from "./pages/Portal/Pilot/ConsultantTracking";
import ConsultantTrackingDetail from "./pages/Portal/Pilot/ConsultantTrackingDetail";
import Performance from "./pages/Portal/Pilot/Performance";
import PerformanceDetail from "./pages/Portal/Pilot/PerformanceDetail";
import PilotTasks from "./pages/Portal/Pilot/Tasks";
import ConsultantDashboard from "./pages/Portal/Consultant/Dashboard";
import ConsultantActivityReports from "./pages/Portal/Consultant/ActivityReports";
import ConsultantLeaveRequests from "./pages/Portal/Consultant/LeaveRequests";
import ConsultantAssignmentFeedback from "./pages/Portal/Consultant/AssignmentFeedback";
import ConsultantCalendar from "./pages/Portal/Consultant/Calendar";
const ActionHandler = React.lazy(() => import("./pages/Auth/ActionHandler"));

const TermsPlaceholder = () => {
  const { t, i18n } = useTranslation("public");
  React.useEffect(() => {
    const explicit = window.localStorage.getItem("portalLanguage");
    if (!explicit) i18n.changeLanguage("fr");
  }, [i18n]);
  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.md">
        <Box bg="white" borderRadius="lg" borderWidth="1px" p={8} shadow="sm">
          <Heading size="lg" mb={4}>{t("landing.terms.title")}</Heading>
          <Text color="gray.600">{t("landing.terms.placeholder")}</Text>
        </Box>
      </Container>
    </Box>
  );
};

function App() {
  const location = useLocation();

  // Marketing pages (landing, module showcases, articles, legal, auth) render a
  // `.mk-page` wrapper and set `body.marketing-page` via MarketingLayout. The
  // prerendered `build/index.html` keeps that class so `/` paints fully styled,
  // but it also doubles as the SPA fallback for every non-prerendered route
  // (/portal/*, /admin/*, /evaluate/*, …). As soon as one of those routes is on
  // screen, strip the marketing body classes so it can't inherit landing styles
  // (e.g. white headings). Runs before paint to avoid a one-frame flash.
  useLayoutEffect(() => {
    if (!document.querySelector(".mk-page")) {
      document.body.classList.remove("marketing-page", "is-auth", "is-contact");
    }
  }, [location.pathname]);

  return (
    <HelmetProvider>
      <SessionExpiredModal />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/resource/logiciel-ATS-optimisation-CV" element={<LogicielATS />} />
        <Route path="/evaluate/:uuid/welcome" element={<WelcomePage />} />
        <Route path="/evaluate/:uuid/mic-check" element={<MicCheckPage />} />
        <Route path="/evaluate/:uuid/sample" element={<SampleQuestionPage />} />
        <Route path="/evaluate/:uuid/start" element={<StartEvaluation />} />
        <Route path="/evaluate/:uuid/q/:qid" element={<QuestionPage />} />
        <Route path="/evaluate/:uuid/submit" element={<SubmitResults />} />
        <Route path="/evaluate/:uuid/success" element={<SuccessPage />} />
        <Route path="/evaluate/:uuid/summary" element={<GroupSummary />} />
        <Route path="/resource/MentionsLegales" element={<MentionsLegales />} />
        <Route path="/resource/Politique" element={<Politique />} />
        
        {/* Module pages */}
        <Route path="/modules/asap-cv" element={<AsapCVModule />} />
        <Route path="/modules/asap-skills" element={<AsapSkillsModule />} />
        <Route path="/modules/asap-pilot" element={<AsapPilot />} />
        <Route path="/modules/asap-partners" element={<AsapPartners />} />
        <Route path="/modules/asap-profil" element={<AsapProfil />} /> 
        <Route path="/signup" element={<SignUp />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/auth/action"
          element={
            <Suspense fallback={<div>Loading…</div>}>
              <ActionHandler />
            </Suspense>
          }
        />

        {/* Partners public landing pages (no auth) */}
        <Route path="/partners/apply/:token" element={<PartnersLandingWelcome />} />
        <Route path="/partners/apply/:token/form" element={<PartnersLandingForm />} />
        <Route path="/partners/apply/:token/success" element={<PartnersLandingSuccess />} />
        <Route path="/partners/unsubscribe/:token" element={<PartnersUnsubscribe />} />

        {/* T&C placeholder page */}
        <Route path="/terms" element={<TermsPlaceholder />} />

        {/* Portal routes */}
        <Route path="/portal" element={<PrivateRoute><PortalLayout /></PrivateRoute>}>
          <Route index element={<PortalIndexRedirect />} />
          <Route path="dashboard" element={<InternalRoute><Dashboard /></InternalRoute>} />
          <Route path="consultant/dashboard" element={<ConsultantRoute><ConsultantDashboard /></ConsultantRoute>} />
          <Route path="consultant/activity-reports" element={<ConsultantRoute><ConsultantActivityReports /></ConsultantRoute>} />
          <Route path="consultant/leave-requests" element={<ConsultantRoute><ConsultantLeaveRequests /></ConsultantRoute>} />
          <Route path="consultant/assignment-feedback" element={<ConsultantRoute><ConsultantAssignmentFeedback /></ConsultantRoute>} />
          <Route path="consultant/calendar" element={<ConsultantRoute><ConsultantCalendar /></ConsultantRoute>} />
          <Route path="profile" element={<ProfileSettings />} />
          <Route path="settings" element={<InternalRoute><AdminRoute><Settings /></AdminRoute></InternalRoute>} />
          <Route path="asap-cv" element={<InternalRoute><FeatureRoute featureKey="has_cv"><AsapCV /></FeatureRoute></InternalRoute>} />
          <Route path="asap-profile" element={<InternalRoute><FeatureRoute featureKey="has_profile"><AsapProfile /></FeatureRoute></InternalRoute>} />
          <Route path="asap-search" element={<InternalRoute><FeatureRoute featureKey="has_profile"><Navigate to="/portal/asap-profile" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-skills" element={<InternalRoute><FeatureRoute featureKey="has_skills"><AsapSkills /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><Navigate to="/portal/asap-pilot/clients" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/clients" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><PilotClients /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/clients/:clientId" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><ClientDetail /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/needs" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><PilotNeeds /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/needs/:id" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><NeedDetail /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/consultants" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><PilotConsultants /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/consultants/:consultantId" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><ConsultantDetail /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/consultant-tracking" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><ConsultantTracking /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/consultant-tracking/:consultantId" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><ConsultantTrackingDetail /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/performance" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><Performance /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/performance/:userId" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><PerformanceDetail /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/tasks" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><PilotTasks /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/matches" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><PilotMatches /></FeatureRoute></InternalRoute>} />
          <Route path="asap-pilot/matches/:matchId" element={<InternalRoute><FeatureRoute featureKey={["has_pilot", "has_pilot_license"]}><MatchDetail /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners" element={<InternalRoute><FeatureRoute featureKey="has_partners"><Navigate to="/portal/asap-partners/sent-requests" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/pools-and-partners" element={<InternalRoute><FeatureRoute featureKey="has_partners"><PartnersPoolsAndCompanies /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/sent-requests" element={<InternalRoute><FeatureRoute featureKey="has_partners"><PartnersMissions /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/received-requests" element={<InternalRoute><FeatureRoute featureKey="has_partners"><PartnersIncoming /></FeatureRoute></InternalRoute>} />
          {/* Backward compatibility redirects for old routes */}
          <Route path="asap-partners/missions" element={<InternalRoute><FeatureRoute featureKey="has_partners"><Navigate to="/portal/asap-partners/sent-requests" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/requirements" element={<InternalRoute><FeatureRoute featureKey="has_partners"><Navigate to="/portal/asap-partners/received-requests" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/incoming" element={<InternalRoute><FeatureRoute featureKey="has_partners"><Navigate to="/portal/asap-partners/received-requests" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/job-postings" element={<InternalRoute><FeatureRoute featureKey="has_partners"><Navigate to="/portal/asap-partners/sent-requests" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/communications" element={<InternalRoute><FeatureRoute featureKey="has_partners"><Navigate to="/portal/asap-partners/sent-requests" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/pools" element={<InternalRoute><FeatureRoute featureKey="has_partners"><Navigate to="/portal/asap-partners/pools-and-partners" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/partners" element={<InternalRoute><FeatureRoute featureKey="has_partners"><Navigate to="/portal/asap-partners/pools-and-partners" replace /></FeatureRoute></InternalRoute>} />
          <Route path="asap-partners/dashboard" element={<InternalRoute><FeatureRoute featureKey="has_partners"><Navigate to="/portal" replace /></FeatureRoute></InternalRoute>} />
          {/* Backward compatibility */}
          <Route path="cv-module" element={<InternalRoute><Navigate to="/portal/asap-cv" replace /></InternalRoute>} />
          <Route path="documents" element={<InternalRoute><Navigate to="/portal/asap-cv" replace /></InternalRoute>} />
        </Route>
        {/* Super Admin routes */}
        <Route path="/admin" element={<SuperAdminRoute><SuperAdminLayout /></SuperAdminRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="configuration" element={<Configuration />} />
          <Route path="companies/:companyId" element={<CompanyDetail />} />
          <Route path="templates" element={<Templates />} />
          <Route path="users" element={<Users />} />
        </Route>
      </Routes>
    </HelmetProvider>
  );
}

export default App;
